import { computeEntriesViewState, EntrySortingType, sortEntries, StagedEntryState, useTranslation } from "@next-platform/client-common-core";
import { Button, ButtonSize, ButtonVariant, Spinner, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { LorebookEntry } from "@next-platform/packages-ts-types";
import { memo, useCallback, useMemo } from "react";

import Trans from "@/components/Trans/Trans";

import EntriesCreateHeader from "./EntriesCreateHeader";
import EntriesListEmptyState from "./EntriesList/EntriesListEmptyState";
import EntriesListHeader from "./EntriesList/EntriesListHeader";
import EntriesListLoadMoreError from "./EntriesList/EntriesListLoadMoreError";
import EntriesListSkeleton from "./EntriesList/EntriesListSkeleton";
import EntryListItem, { FilteredEntryWithIndex } from "./EntryListItem";

interface IEntriesProps {
  entries: LorebookEntry[];
  // Staged badge per entry id (public-lorebook staging flow); absent → no badge on that row.
  stagedStateByEntryId?: Map<string, StagedEntryState>;
  hideCreateEntryButton?: boolean;
  onOpenModal?: (index?: number) => void;
  totalEntries?: number; // Backend total (num_entries); authoritative for count and the entry limit
  isProcessingEntries?: boolean;
  isFormDisabled?: boolean;
  hideWarning?: boolean;
  showNoEntriesError?: boolean;
  isUnderReview?: boolean;
  // Server-driven search + pagination state, owned by useLorebookEntries in LorebookEdit
  searchValue?: string;
  onSearchChange?: (next: string) => void;
  hasMore?: boolean;
  isLoadingFirstPage?: boolean;
  isLoadingMore?: boolean;
  hasError?: boolean;
  onLoadMore?: () => void;
  onRetry?: () => void;
  // Sort dropdown. PRIORITY is served by a server-side refetch (owned by LorebookEdit via
  // onSortChange); the remaining options sort the loaded entries in memory.
  sortType?: EntrySortingType;
  onSortChange?: (next: EntrySortingType) => void;
  isPrioritySortAvailable?: boolean;
}

function EntriesProcessing() {
  return (
    <div className="flex justify-center items-center flex-col gap-lg h-[200px]">
      <Spinner size="large" />
      <div>
        <Text variant={TextVariants.LABEL_LARGE_REGULAR} className="text-gray-12">
          <Trans i18nKey="lorebook:entries.processing" />
        </Text>
        <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-10">
          <Trans i18nKey="lorebook:entries.processingDescription" />
        </Text>
      </div>
    </div>
  );
}

type EntriesRowsProps = {
  rows: FilteredEntryWithIndex[];
  showSkeleton: boolean;
  onOpenModal?: (index?: number) => void;
  isFormDisabled?: boolean;
  hideWarning: boolean;
  stagedStateByEntryId?: Map<string, StagedEntryState>;
};

const EntriesRows = memo(function EntriesRows({
  rows,
  showSkeleton,
  onOpenModal,
  isFormDisabled,
  hideWarning,
  stagedStateByEntryId,
}: EntriesRowsProps) {
  if (showSkeleton) {
    return <EntriesListSkeleton />;
  }
  if (rows.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-col">
      {rows.map((row) => (
        <EntryListItem
          key={row.entry.id || row.originalIndex}
          filteredEntry={row}
          onOpenModal={onOpenModal}
          isFormDisabled={isFormDisabled}
          hideWarning={hideWarning}
          stagedState={stagedStateByEntryId?.get(row.entry.id)}
        />
      ))}
    </div>
  );
});

type EntriesFooterProps = {
  isLoadingMore: boolean;
  shouldShowEmptyState: boolean;
  isLoadMoreErrorVisible: boolean;
  isRefreshErrorVisible: boolean;
  shouldShowLoadMore: boolean;
  isSearchActive: boolean;
  searchQuery: string;
  hasError: boolean;
  onRetry: () => void;
  onLoadMore: () => void;
};

const EntriesFooter = memo(function EntriesFooter({
  isLoadingMore,
  shouldShowEmptyState,
  isLoadMoreErrorVisible,
  isRefreshErrorVisible,
  shouldShowLoadMore,
  isSearchActive,
  searchQuery,
  hasError,
  onRetry,
  onLoadMore,
}: EntriesFooterProps) {
  const { t } = useTranslation();

  const emptyStateText = useMemo(() => {
    if (hasError) {
      return t("lorebook:entries.loadError");
    }
    if (isSearchActive) {
      return t("lorebook:entries.noResultsForSearch", { query: searchQuery });
    }
    return t("lorebook:entries.emptyState");
  }, [hasError, isSearchActive, searchQuery, t]);

  return (
    <>
      {isLoadingMore ? (
        <div className="flex justify-center py-4" data-testid="Entries-LoadMoreLoader">
          <Spinner size="large" />
        </div>
      ) : null}
      {shouldShowEmptyState ? <EntriesListEmptyState message={emptyStateText} hasError={hasError} onRetry={onRetry} /> : null}
      {isLoadMoreErrorVisible ? <EntriesListLoadMoreError message={t("lorebook:entries.loadMoreError")} onRetry={onLoadMore} /> : null}
      {isRefreshErrorVisible ? <EntriesListLoadMoreError message={t("lorebook:entries.loadError")} onRetry={onRetry} /> : null}
      {shouldShowLoadMore ? (
        <div className="flex justify-center mt-4">
          <Button variant={ButtonVariant.OUTLINED} size={ButtonSize.MEDIUM} className="w-full" onClick={onLoadMore}>
            <Trans i18nKey="lorebook:entries.loadMore" />
          </Button>
        </div>
      ) : null}
    </>
  );
});

export default function Entries({
  entries,
  stagedStateByEntryId,
  hideCreateEntryButton,
  onOpenModal,
  totalEntries: totalEntriesProp,
  isProcessingEntries,
  isFormDisabled,
  hideWarning = false,
  showNoEntriesError = false,
  isUnderReview = false,
  searchValue = "",
  onSearchChange,
  hasMore = false,
  isLoadingFirstPage = false,
  isLoadingMore = false,
  hasError = false,
  onLoadMore,
  onRetry,
  sortType = EntrySortingType.PRIORITY,
  onSortChange,
  isPrioritySortAvailable = false,
}: IEntriesProps) {
  const {
    totalEntries,
    hasReachedEntryLimit,
    isSearchActive,
    shouldShowEmptyState,
    isLoadMoreErrorVisible,
    isRefreshErrorVisible,
    shouldShowLoadMore,
    shouldShowSkeleton,
  } = computeEntriesViewState({
    entryCount: entries.length,
    totalEntriesProp,
    searchValue,
    hasMore,
    isLoadingFirstPage,
    isLoadingMore,
    hasError,
  });

  // Visible count excludes staged REMOVED rows: they stay in the list (struck through) but are
  // subtracted from totalEntries/effectiveTotal, so counting them here would let the "showing X of Y"
  // header report X > Y during an active search.
  const visibleEntryCount = useMemo(
    () => entries.filter((entry) => stagedStateByEntryId?.get(entry.id) !== StagedEntryState.REMOVED).length,
    [entries, stagedStateByEntryId],
  );

  // originalIndex maps a row back to its position in the loaded `entries` array so the edit modal
  // opens the correct entry. It is captured BEFORE sorting and carried through, so reordering the
  // display never makes the modal open the wrong entry. Precomputed (not inline in JSX) to keep
  // referential stability.
  const entryRows = useMemo<FilteredEntryWithIndex[]>(() => {
    const rows = entries.map((entry, index) => ({ entry, originalIndex: index }));
    // Staged creates (NEW) are pinned to the top regardless of sort — they have no timestamp, so
    // Latest/Oldest/Name sorting would otherwise scatter them and the NEW badge would drift away
    // from where the user just added the row. Only the live entries participate in the sort.
    const newRows = rows.filter((row) => stagedStateByEntryId?.get(row.entry.id) === StagedEntryState.NEW);
    const sortableRows = rows.filter((row) => stagedStateByEntryId?.get(row.entry.id) !== StagedEntryState.NEW);
    const sorted = sortEntries(
      sortableRows.map((row) => row.entry),
      sortType,
    );
    // Re-pair each sorted entry with its pre-sort index. Identity lookup (not a name/id match) so
    // duplicate names — which this data has — cannot map a row to the wrong entry.
    const indexByEntry = new Map(rows.map((row) => [row.entry, row.originalIndex]));
    const sortedRows = sorted.map((entry) => ({ entry, originalIndex: indexByEntry.get(entry) ?? -1 }));
    return [...newRows, ...sortedRows];
  }, [entries, sortType, stagedStateByEntryId]);

  const handleOpenCreateEntry = useCallback(() => {
    onOpenModal?.();
  }, [onOpenModal]);

  const handleSearchChange = useCallback(
    (value: string) => {
      onSearchChange?.(value);
    },
    [onSearchChange],
  );

  const handleLoadMore = useCallback(() => {
    onLoadMore?.();
  }, [onLoadMore]);

  const handleRetry = useCallback(() => {
    onRetry?.();
  }, [onRetry]);

  if (isProcessingEntries) {
    return <EntriesProcessing />;
  }

  const createHeader = hideCreateEntryButton ? null : (
    <EntriesCreateHeader
      onOpenCreateEntry={handleOpenCreateEntry}
      hasReachedEntryLimit={hasReachedEntryLimit}
      showNoEntriesError={showNoEntriesError}
      isUnderReview={isUnderReview}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      <EntriesListHeader
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        totalEntryCount={totalEntries}
        visibleEntryCount={visibleEntryCount}
        listHeader={createHeader}
        isSortVisible={true}
        sortType={sortType}
        onSortChange={onSortChange}
        isPrioritySortAvailable={isPrioritySortAvailable}
      />

      <EntriesRows
        rows={entryRows}
        showSkeleton={shouldShowSkeleton}
        onOpenModal={onOpenModal}
        isFormDisabled={isFormDisabled}
        hideWarning={hideWarning}
        stagedStateByEntryId={stagedStateByEntryId}
      />

      <EntriesFooter
        isLoadingMore={isLoadingMore}
        shouldShowEmptyState={shouldShowEmptyState}
        isLoadMoreErrorVisible={isLoadMoreErrorVisible}
        isRefreshErrorVisible={isRefreshErrorVisible}
        shouldShowLoadMore={shouldShowLoadMore}
        isSearchActive={isSearchActive}
        searchQuery={searchValue.trim()}
        hasError={hasError}
        onRetry={handleRetry}
        onLoadMore={handleLoadMore}
      />
    </div>
  );
}
